import { FC, ReactElement, useMemo } from 'react';

import { AccountLink } from '@sparkplug/lib';

import { NoRetailersFoundGraphic } from '@components/graphics';
import EmptyStateDisplay from '@components/layout/EmptyStateDisplay';
import Grid from '@components/layout/Grid';
import toast from '@components/toast';
import Toolbar from '@components/toolbar/Toolbar';

import { useApp } from '@hooks/AppHooks';
import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks';
import { useSearch } from '@hooks/UIHooks';

import { sortByString } from '@helpers/ui';
import { getStateNameByAbbreviation, isEmpty } from '@helpers/util';

import { useCreateAccountLink, useSendAccountLinkRequest } from '../../../mutations';
import { useSuggestedAccountLinks } from '../../../queries';
import { addAccountLinkSearchableFields } from '../../../utils';
import BrandLinkCard from './BrandLinkCard';

import './AdminAccountLinksSuggestedView.scss';

export interface IAdminAccountLinksSuggestedViewProps {
    otherAccounts: AccountLink[];
    toolbarMarkets: ReactElement;
    filteredMarkets: string[];
}

const AdminAccountLinksSuggestedView: FC<IAdminAccountLinksSuggestedViewProps> = ({
    otherAccounts,
    toolbarMarkets,
    filteredMarkets,
}) => {
    // This lets us search the `otherAccounts` array by brand name, or maybe other keys in the future...?
    const searchableAccounts = useMemo(() => {
        return addAccountLinkSearchableFields(otherAccounts);
    }, [otherAccounts]);

    const { history } = useApp();

    const { account: currentAccount, userCan } = useSparkplugAccount();
    const userCanManageLinks = userCan('manageLinks');

    const { isCreatingAccountLink, createAccountLinkAsync } = useCreateAccountLink(
        currentAccount?._id || '',
    );
    const { isSendingAccountLinkRequest, sendAccountLinkRequestAsync } = useSendAccountLinkRequest(
        currentAccount?._id || '',
    );
    const { refetchSuggestedAccountLinks } = useSuggestedAccountLinks(currentAccount?._id || '');
    const { searchFilter, onChangeSearchFilter, applySearch } = useSearch([
        'accountName',
        'mappedNamesStr',
    ]);

    const onCardButtonClick = (prospectiveAccountLink: AccountLink) => {
        if (currentAccount?.type === 'retailer') {
            const accountId = prospectiveAccountLink.accountId;
            toast.promise(
                createAccountLinkAsync(
                    {
                        vendorAccountId: accountId,
                        body: {},
                    },
                    {
                        onSuccess: () => {
                            refetchSuggestedAccountLinks();
                            // Note: we replace here because when the user goes "back" we'd rather have them to go the main links page instead of the suggested links page
                            history.replace(
                                `/${currentAccount._id}/settings/vendor/${prospectiveAccountLink.accountId}/manage?isNewVendorLink=true`,
                            );
                        },
                    },
                ),
                {
                    loading: 'Creating link...',
                    success: `Vendor link created with ${prospectiveAccountLink.accountName}`,
                    error: 'Something went wrong - please try again and contact support if this issue persists.',
                },
            );
        }

        if (currentAccount?.type === 'brand') {
            const accountId = prospectiveAccountLink.accountId;
            toast.promise(
                sendAccountLinkRequestAsync(accountId, {
                    onSuccess: () => {
                        refetchSuggestedAccountLinks();
                    },
                }),
                {
                    loading: 'Sending request...',
                    success: `Retailer link request sent to ${prospectiveAccountLink.accountName}`,
                    error: 'Something went wrong - please try again and contact support if this issue persists.',
                },
            );
        }
    };

    const filteredMarketsDict: any = useMemo(() => {
        return filteredMarkets.reduce((dict, filteredMarket) => {
            return {
                ...dict,
                [filteredMarket]: true,
            };
        }, {});
    }, [filteredMarkets]);

    const filteredAccounts: AccountLink[] = useMemo(() => {
        const searchedAccounts: AccountLink[] = applySearch(searchableAccounts);

        return searchedAccounts
            .filter((searchedAccount) => {
                const sharedMarkets = searchedAccount?.markets || [];

                // Check if the current `searchedAccount` has `sharedMarkets` that
                // coincide with the current `filteredMarkets`
                return sharedMarkets.some((sharedMarket: string) => {
                    const sharedMarketLabel =
                        getStateNameByAbbreviation(sharedMarket) || sharedMarket;
                    return Boolean(filteredMarketsDict[sharedMarketLabel]);
                });
            })
            .sort(sortByString('accountName', 'asc'));
    }, [otherAccounts, applySearch, filteredMarketsDict]);

    return (
        <>
            <Toolbar justifyContentStart>
                <Toolbar.Search
                    name="search"
                    defaultValue={searchFilter}
                    onChange={onChangeSearchFilter}
                />
                <Toolbar.Text className="account-markets toolbar-group-start">
                    {toolbarMarkets}
                </Toolbar.Text>
            </Toolbar>

            {!isEmpty(filteredAccounts) ? (
                <Grid className="account-links-grid">
                    {filteredAccounts.map((otherAccount, i) => (
                        <Grid.Item key={i} sm={3}>
                            <BrandLinkCard
                                account={otherAccount}
                                onButtonClick={onCardButtonClick}
                                disableCard={
                                    isSendingAccountLinkRequest ||
                                    isCreatingAccountLink ||
                                    (currentAccount?.type === 'retailer' && !userCanManageLinks)
                                }
                                allowLinkCreate
                            />
                        </Grid.Item>
                    ))}
                </Grid>
            ) : (
                <EmptyStateDisplay
                    className="admin-brand-links-empty-state"
                    graphic={<NoRetailersFoundGraphic />}
                    label={
                        currentAccount?.type === 'brand' ? 'No Retailers Found' : 'No Vendors Found'
                    }
                />
            )}
        </>
    );
};

export default AdminAccountLinksSuggestedView;
